<template>
    <BaseButton
        :theme="baseTheme"
        :size="size"
        :icon-only="iconOnly"
        class="journey-button"
        :class="[`theme-${theme}`, { 'is-pill': pill, 'is-detail': detail, 'is-text-ghost': theme === 'ghost' && !iconOnly }]">
        <slot />
    </BaseButton>
</template>

<script setup lang="ts">
import BaseButton, { type ButtonTheme } from "~vue/components/BaseButton.vue";
import { computed } from "vue";

type JourneyButtonTheme = "primary" | "secondary" | "neutral" | "tertiary" | "ghost" | "danger" | "danger-text" | "done" | "selected" | "inverse";

const {
    theme = "neutral",
    size = "sm",
    iconOnly = false,
    pill = false,
    detail = false,
} = defineProps<{
    theme?: JourneyButtonTheme;
    size?: "sm" | "md";
    iconOnly?: boolean;
    pill?: boolean;
    detail?: boolean;
}>();

const baseThemes: Record<JourneyButtonTheme, ButtonTheme> = {
    primary: "primary",
    secondary: "secondary",
    neutral: "neutral",
    tertiary: "tertiary",
    ghost: "ghost",
    danger: "danger",
    "danger-text": "tertiary",
    done: "neutral",
    selected: "neutral",
    inverse: "neutral",
};
const baseTheme = computed(() => baseThemes[theme]);
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .journey-button {
        --radius-button: 0.5rem;
        --font-weight-button: 500;
        --font-weight-button-sm: 500;
        --text-button: 14px;
        --leading-button: 1.43;
        --spacing-button-x: 1rem;
        --spacing-button-y: 0.625rem;
        --spacing-button-sm-y: 0.3125rem;
    }

    .is-pill {
        --radius-button: 9999px;
    }

    .is-text-ghost:where(:enabled) {
        @apply text-ink-muted;
    }

    .is-detail {
        --text-button-sm: 12px;
        --leading-button-sm: 1.5;
        --spacing-button-sm-x: 0;
        --spacing-button-sm-y: 0;
    }

    .theme-done {
        --color-button-neutral: var(--color-chip-status-done-bg);
        --color-button-neutral-text: var(--color-chip-status-done-text);
        --color-button-neutral-hover: var(--color-journey-done-hover);
        --color-button-neutral-border: transparent;
        --radius-button: 9999px;
    }

    .theme-selected {
        --color-button-neutral: var(--color-select-option-selected);
        --color-button-neutral-text: var(--color-control-text-strong);
        --color-button-neutral-hover: var(--color-select-option-selected);
        --color-button-neutral-border: transparent;
    }

    .theme-danger-text {
        --color-button-tertiary-text: var(--color-button-danger);
        --color-button-tertiary-hover-text: var(--color-button-danger-hover);
    }

    .theme-inverse {
        --color-button-neutral: var(--color-surface-raised);
        --color-button-neutral-text: var(--color-ink);
        --color-button-neutral-border: transparent;
    }
}
</style>
